import React, { useState, useEffect, useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getApplicantById } from '../services/api';
import { useBrandingColors } from '../../hooks/useBrandingColors';

interface EStaffResumeModalProps {
  open: boolean;
  onClose: () => void;
  resumeData: any;
  loading?: boolean;
  searchKeywords?: string[];
  searchCities?: string[];
}

const isEStaffSkillCode = (value: string): boolean => /^skill_type_\d+$/i.test(value.trim());

const normalizeSkillLabel = (skill: any): string | null => {
  if (typeof skill === 'string' || typeof skill === 'number') {
    const label = String(skill).trim();
    return label && !isEStaffSkillCode(label) ? label : null;
  }

  if (skill && typeof skill === 'object') {
    for (const field of ['name', 'typeName', 'type_name', 'skillName', 'skill_name', 'title']) {
      const label = String(skill[field] ?? '').trim();
      if (label && !isEStaffSkillCode(label)) return label;
    }
  }

  return null;
};

const normalizeSkills = (value: any): string[] => {
  const items = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];

  return items
    .map(normalizeSkillLabel)
    .filter((skill): skill is string => Boolean(skill));
};

const EStaffResumeModal: React.FC<EStaffResumeModalProps> = ({
  open,
  onClose,
  resumeData,
  loading = false,
  searchKeywords = [],
  searchCities = []
}) => {
  const colors = useBrandingColors();
  const [resume, setResume] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Найденные ключевые слова в резюме
  const matchedKeywords = useMemo(() => {
    if (!resume) return [];
    const text = String(JSON.stringify(resume) || '');
    const kws = Array.isArray(searchKeywords) ? searchKeywords : [];
    const found: string[] = [];
    const seen = new Set<string>();
    for (const kw of kws) {
      const s = String(kw ?? '').trim();
      if (!s) continue;
      const key = s.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const escapedKeyword = s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(^|[^а-яёА-ЯЁa-zA-Z0-9_])(${escapedKeyword})`, 'iu');
      if (regex.test(text)) found.push(s);
    }
    return found;
  }, [resume, searchKeywords]);

  useEffect(() => {
    if (open && resumeData?.id) {
      fetchResume();
    }
  }, [open, resumeData]);

  const fetchResume = async () => {
    if (!resumeData?.id) return;

    try {
      setIsLoading(true);
      setError(null);
      const data = await getApplicantById(resumeData.id, resumeData.source || 'estaff');
      setResume(data);
    } catch (err: any) {
      const errorMessage = typeof err.response?.data?.error === 'string'
        ? err.response?.data?.error
        : err.response?.data?.message || err.message || 'Ошибка загрузки резюме';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Проверка, соответствует ли город фильтру поиска
  const isCityMatched = (cityName: string | undefined) => {
    if (!cityName) return false;
    if (!searchCities || searchCities.length === 0) return false;
    return searchCities.some(searchCity => {
      if (cityName.toLowerCase().includes(searchCity.toLowerCase())) return true;
      if (searchCity.toLowerCase().includes(cityName.toLowerCase())) return true;
      return false;
    });
  };

  // Подсветка ключевых слов
  const highlightText = (text: string) => {
    const textStr = typeof text === 'string' ? text :
                    typeof text === 'object' && text !== null ? JSON.stringify(text) :
                    String(text || '');

    if (!textStr || !searchKeywords || searchKeywords.length === 0) {
      return textStr;
    }

    const keywords = Array.isArray(searchKeywords) ? searchKeywords : [searchKeywords];
    const validKeywords = keywords.filter(Boolean);
    if (validKeywords.length === 0) return textStr;

    // Собираем один regex из всех ключевых слов
    const escapedKeywords = validKeywords.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const regex = new RegExp(`(${escapedKeywords.join('|')})`, 'giu');

    // Разбиваем текст на части и оборачиваем совпадения в React-элементы (без dangerouslySetInnerHTML)
    const parts = textStr.split(regex);
    return (
      <span>
        {parts.map((part, i) =>
          regex.test(part)
            ? <mark key={i} className="bg-yellow-300">{part}</mark>
            : part
        )}
      </span>
    );
  };

  // Форматирование даты
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU', {
        day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Форматирование опыта в годы/месяцы
  const formatExperience = (months: number) => {
    if (!months || months <= 0) return 'без опыта';
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    const parts: string[] = [];
    if (years > 0) parts.push(`${years} лет`);
    if (remainingMonths > 0) parts.push(`${remainingMonths} мес`);
    return parts.join(' ');
  };

  if (!open) return null;

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-3xl p-6 flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: colors.primary }}></div>
          <p className="mt-4 text-center">Загрузка резюме...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-3xl p-6 max-w-md">
          <div className="flex items-start space-x-3">
            <svg className="h-6 w-6 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Ошибка загрузки</h3>
              <p className="text-gray-600">{error}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="mt-6 w-full px-4 py-2 text-white rounded-3xl font-medium transition-colors"
            style={{ backgroundColor: colors.primary }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primaryHover; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primary; }}
          >
            Закрыть
          </button>
        </div>
      </div>
    );
  }

  // Имя кандидата
  const fullName = resume?.fullName || resume?.name ||
    [resume?.lastName, resume?.firstName, resume?.middleName].filter(Boolean).join(' ') ||
    'Имя не указано';

  // Навыки
  const skills: string[] = (() => {
    const skillsets = normalizeSkills(resume?.skillsets);
    if (skillsets.length > 0) return skillsets;
    return normalizeSkills(resume?.skills);
  })();

  // Опыт работы
  const experiences = resume?.experiences || resume?.experience || [];

  // Образование
  const educations = resume?.educations || resume?.education?.primary || [];

  // Контакты
  let email = resume?.email;
  let phone = resume?.phone;
  if (resume?.contact && Array.isArray(resume.contact)) {
    resume.contact.forEach((contact: any) => {
      if (contact.type?.id === 'email') email = contact.value;
      else if (contact.type?.id === 'cell' || contact.type?.id === 'phone') {
        phone = contact.value?.formatted || contact.value;
      }
    });
  }

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {resume?.position ? highlightText(resume.position) : 'Резюме'}
            </h2>
            <p className="text-gray-600 mt-1">{fullName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Левая колонка */}
            <div>
              {/* Основная информация */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Основная информация</h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  {resume?.gender && (
                    <p><span className="font-medium">Пол:</span> {resume.gender === 'male' ? 'Мужской' : resume.gender === 'female' ? 'Женский' : resume.gender}</p>
                  )}
                  {resume?.age && (
                    <p><span className="font-medium">Возраст:</span> {resume.age}</p>
                  )}
                  {resume?.area?.name && (
                    <p>
                      <span className="font-medium">Город:</span>{' '}
                      <span className={isCityMatched(resume.area.name) ? 'bg-green-200 px-1 rounded' : ''}>
                        {resume.area.name}
                      </span>
                    </p>
                  )}
                  {resume?.salary && (
                    <p><span className="font-medium">Зарплата:</span> {resume.salary.amount?.toLocaleString()} {resume.salary.currency}</p>
                  )}
                  {resume?.totalExperienceMonths && (
                    <p><span className="font-medium">Опыт:</span> {formatExperience(resume.totalExperienceMonths)}</p>
                  )}
                  {resume?.educationLevel?.name && (
                    <p><span className="font-medium">Образование:</span> {resume.educationLevel.name}</p>
                  )}
                </div>
              </div>

              {/* Контакты */}
              {(email || phone) && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Контакты</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    {email && (
                      <p><span className="font-medium">Email:</span> {email}</p>
                    )}
                    {phone && (
                      <p><span className="font-medium">Телефон:</span> {phone}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Гражданство */}
              {resume?.citizenships && resume.citizenships.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Гражданство</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p>{resume.citizenships.map((c: any) => c.name).join(', ')}</p>
                  </div>
                </div>
              )}

              {/* Языки */}
              {resume?.languages && resume.languages.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Языки</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-1">
                    {resume.languages.map((lang: any, index: number) => (
                      <p key={index}>
                        <span className="font-medium">{lang.name}</span>
                        {lang.level && `: ${lang.level}`}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Навыки */}
              {skills.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Навыки</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      {skills.map((skill: string, index: number) => (
                        <span
                          key={index}
                          className="px-3 py-1 rounded-full text-sm"
                          style={{
                            backgroundColor: `${colors.primary}20`,
                            color: colors.primary,
                            border: `1px solid ${colors.primary}40`
                          }}
                        >
                          {highlightText(typeof skill === 'string' ? skill.trim() : String(skill))}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* О себе */}
              {resume?.body && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">О себе</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700 whitespace-pre-wrap">{highlightText(resume.body)}</p>
                  </div>
                </div>
              )}

              {/* Образование */}
              {educations.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Образование</h3>
                  <div className="space-y-4">
                    {educations.map((edu: any, index: number) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg">
                        <p className="font-semibold text-gray-900">{highlightText(edu.name || '')}</p>
                        {edu.organization && <p className="text-gray-600">{highlightText(edu.organization)}</p>}
                        {edu.result && <p className="text-gray-600">{highlightText(edu.result)}</p>}
                        {edu.year && <p className="text-sm text-gray-500">Год окончания: {edu.year}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* История статусов */}
              {resume?.statusHistory && resume.statusHistory.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">История статусов</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    {resume.statusHistory
                      .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())
                      .map((status: any, index: number) => (
                        <div key={index} className="flex items-center justify-between py-1 border-b last:border-0">
                          <span className="text-sm font-medium text-gray-900">
                            {status.status?.name || 'Статус'}
                          </span>
                          <span className="text-sm text-gray-500">
                            {formatDate(status.date)}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Правая колонка */}
            <div>
              {/* Опыт работы */}
              {experiences.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Опыт работы</h3>
                  <div className="space-y-4">
                    {experiences.map((exp: any, index: number) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg">
                        <p className="font-semibold text-gray-900">{highlightText(exp.position || '')}</p>
                        <div className="flex items-center gap-2 text-gray-600">
                          <span>{highlightText(exp.company || '')}</span>
                          {exp.area?.name && (
                            <span className={`text-sm px-2 py-0.5 rounded ${isCityMatched(exp.area?.name) ? 'bg-green-200' : 'bg-gray-200'}`}>
                              {exp.area.name}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {exp.start ? formatDate(exp.start) : ''} — {exp.end ? formatDate(exp.end) : 'по настоящее время'}
                        </p>
                        {exp.description && (
                          <p className="mt-2 text-gray-700 whitespace-pre-wrap">{highlightText(exp.description)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Резюме (HTML от E-Staff). TASKNEIROKLYUCH-787:
                  бэкенд hr-proxy возвращает resume_text (полный HTML резюме от
                  E-Staff, 20K-200K символов), relevanter-back маппит в
                  resumeText. Раньше эта секция отсутствовала — модалка не
                  показывала резюме у всех 11 кандидатов выборки.

                  Рендерим через iframe + sandbox="": MS Office HTML тянет свои
                  CSS-классы и стили, которые без изоляции ломают tailwind-вёрстку
                  модалки. sandbox без 'allow-scripts' блокирует JS внутри HTML
                  (если вдруг E-Staff отдаст резюме с <script>). */}
              {(resume?.resumeText || resume?.body) && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Резюме</h3>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <iframe
                      title="Резюме кандидата"
                      srcDoc={resume.resumeText || resume.body}
                      sandbox=""
                      className="w-full bg-white"
                      style={{ height: '600px', border: 'none' }}
                    />
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t flex justify-between items-center">
          <div className="text-sm text-gray-500 max-w-2xl">
            {matchedKeywords.length > 0 && (
              <div className="space-y-1">
                <div className="flex flex-wrap gap-2">
                  <span className="font-medium">Подсвечено:</span>
                  {matchedKeywords.map((keyword, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <a
              href={`/recruiter/applicant/${resumeData?.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-white rounded-3xl font-medium transition-colors"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = colors.primaryHover; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = colors.primary; }}
            >
              Открыть полностью
            </a>
            <button
              onClick={onClose}
              className="px-4 py-2 text-white rounded-3xl font-medium transition-colors"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primaryHover; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primary; }}
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EStaffResumeModal;
